import { useTranslation } from 'react-i18next';
import { Search, Users } from 'lucide-react';
import type { POSCustomer } from '@/types';
import { formatMoney } from '@/utils/currency';
import { getRegionsForCountry } from '@/data/locales';
import CustomerRowActionsMenu, { CustomerRowActionIcons } from './CustomerRowActionsMenu';

const { BookOpen, Eye, FileText, Pencil, Tag, Trash2, Activity } = CustomerRowActionIcons;

export type CustomerTypeFilter = 'All' | 'Retailer' | 'Wholesale' | 'Distributor';
export type CustomerBalanceFilter = 'all' | 'outstanding' | 'clear';

interface CustomerTableViewProps {
  customers: POSCustomer[];
  isLoading?: boolean;
  isUk?: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  typeFilter: CustomerTypeFilter;
  balanceFilter: CustomerBalanceFilter;
  provinceFilter: string;
  cityFilter: string;
  postalFilter: string;
  onTypeFilterChange: (v: CustomerTypeFilter) => void;
  onBalanceFilterChange: (v: CustomerBalanceFilter) => void;
  onProvinceFilterChange: (v: string) => void;
  onCityFilterChange: (v: string) => void;
  onPostalFilterChange: (v: string) => void;
  canEdit: boolean;
  canDelete: (customer: POSCustomer) => boolean;
  onView: (customer: POSCustomer) => void;
  onEdit: (customer: POSCustomer) => void;
  onDelete: (customer: POSCustomer) => void;
  onLedger: (customer: POSCustomer) => void;
  onRegister: (customer: POSCustomer) => void;
  onPayment: (customer: POSCustomer) => void;
  onInvoices: (customer: POSCustomer) => void;
  onQuotations: (customer: POSCustomer) => void;
  onSpecialPrices: (customer: POSCustomer) => void;
}

export default function CustomerTableView({
  customers,
  isLoading,
  isUk = false,
  search,
  onSearchChange,
  typeFilter,
  balanceFilter,
  provinceFilter,
  cityFilter,
  postalFilter,
  onTypeFilterChange,
  onBalanceFilterChange,
  onProvinceFilterChange,
  onCityFilterChange,
  onPostalFilterChange,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDelete,
  onLedger,
  onRegister,
  onPayment,
  onInvoices,
  onQuotations,
  onSpecialPrices,
}: CustomerTableViewProps) {
  const { t } = useTranslation();
  const regions = getRegionsForCountry(isUk ? 'GB' : undefined);

  const filtered = customers.filter((c) => {
    const outstanding = c.outstanding_balance || 0;
    if (balanceFilter === 'outstanding' && outstanding <= 0) return false;
    if (balanceFilter === 'clear' && outstanding > 0) return false;
    return true;
  });

  return (
    <div className="space-y-3">
      <div className="card p-3 flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <label className="block text-[11px] font-medium text-gray-500 mb-1">
            {t('common.search', 'Search')}
          </label>
          <Search className="absolute left-3 bottom-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder={
              isUk
                ? t(
                    'customers.searchPlaceholderUk',
                    'Search by name, phone, email, city, state or postcode…',
                  )
                : t('customers.searchPlaceholder')
            }
            className="input-field pl-9"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-[160px]">
          <label className="block text-[11px] font-medium text-gray-500 mb-1">
            {t('customers.type', 'Type')}
          </label>
          <select
            className="input-field"
            value={typeFilter}
            onChange={(e) => onTypeFilterChange(e.target.value as CustomerTypeFilter)}
          >
            <option value="All">{t('common.all', 'All')}</option>
            <option value="Retailer">{t('customers.typeRetailer', 'Retailer')}</option>
            <option value="Wholesale">{t('customers.typeWholesale', 'Wholesale')}</option>
            <option value="Distributor">{t('customers.typeDistributor', 'Distributor')}</option>
          </select>
        </div>

        <div className="w-full sm:w-[160px]">
          <label className="block text-[11px] font-medium text-gray-500 mb-1">
            {t('customers.balance', 'Balance')}
          </label>
          <select
            className="input-field"
            value={balanceFilter}
            onChange={(e) => onBalanceFilterChange(e.target.value as CustomerBalanceFilter)}
          >
            <option value="all">{t('common.all', 'All')}</option>
            <option value="outstanding">{t('customers.hasOutstanding', 'Has outstanding')}</option>
            <option value="clear">{t('customers.balanceClear', 'Clear')}</option>
          </select>
        </div>

        {isUk && (
          <>
            <div className="w-full sm:w-[160px]">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">
                {t('common.region', 'State')}
              </label>
              <select
                className="input-field"
                value={provinceFilter}
                onChange={(e) => onProvinceFilterChange(e.target.value)}
              >
                <option value="">{t('common.all', 'All')}</option>
                {regions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-[150px]">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">
                {t('common.city', 'City')}
              </label>
              <input
                type="text"
                className="input-field"
                placeholder={t('common.city', 'City')}
                value={cityFilter}
                onChange={(e) => onCityFilterChange(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-[140px]">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">
                {t('common.postalCode', 'Postal code')}
              </label>
              <input
                type="text"
                className="input-field"
                placeholder={t('common.postalCode', 'Postal code')}
                value={postalFilter}
                onChange={(e) => onPostalFilterChange(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <div className="card overflow-hidden border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('customers.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>{t('customers.empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${isUk ? 'min-w-[1100px]' : 'min-w-[900px]'}`}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                    {t('customers.customer', 'Customer')}
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                    {t('customers.type', 'Type')}
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                    {t('common.phone', 'Phone')}
                  </th>
                  {isUk ? (
                    <>
                      <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                        {t('common.address', 'Address')}
                      </th>
                      <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                        {t('common.city', 'City')}
                      </th>
                      <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                        {t('common.postalCode', 'Postal code')}
                      </th>
                    </>
                  ) : (
                    <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                      {t('customers.location', 'Location')}
                    </th>
                  )}
                  <th className="text-right px-3 py-2.5 font-semibold text-gray-600">
                    {t('customers.totalSales')}
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-gray-600">
                    {t('customers.received')}
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-gray-600">
                    {t('customers.outstanding')}
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-gray-600 w-12">
                    <span className="sr-only">{t('common.actions', 'Actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filtered.map((customer) => {
                  const displayName = customer.is_walk_in
                    ? `${customer.customer_name} ${t('customers.walkIn')}`
                    : customer.customer_name;
                  const location = [customer.postal_code, customer.city].filter(Boolean).join(' · ') || '—';
                  const outstanding = customer.outstanding_balance || 0;

                  return (
                    <tr
                      key={customer.name}
                      className="hover:bg-gray-50/80 cursor-pointer"
                      onClick={() => onView(customer)}
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-gray-900 truncate">{displayName}</span>
                          {Boolean(customer.is_default) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100 shrink-0">
                              {t('common.default')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-gray-600">
                        {customer.customer_type || 'Retailer'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                        {customer.mobile || '—'}
                      </td>
                      {isUk ? (
                        <>
                          <td
                            className="px-3 py-2.5 text-gray-600 truncate max-w-[220px]"
                            title={customer.address || undefined}
                          >
                            {customer.address || '—'}
                          </td>
                          <td className="px-3 py-2.5 text-gray-600 truncate max-w-[140px]">
                            {customer.city || '—'}
                          </td>
                          <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap font-mono text-xs">
                            {customer.postal_code || '—'}
                          </td>
                        </>
                      ) : (
                        <td className="px-3 py-2.5 text-gray-500 truncate max-w-[180px]" title={location}>
                          {location}
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                        {formatMoney(customer.total_sale || 0)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                        {formatMoney(customer.paid_amount || 0)}
                      </td>
                      <td
                        className={`px-3 py-2.5 text-right tabular-nums font-medium ${
                          outstanding > 0 ? 'text-red-600' : 'text-gray-700'
                        }`}
                      >
                        {formatMoney(outstanding)}
                      </td>
                      <td
                        className="px-3 py-2.5 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CustomerRowActionsMenu
                          items={[
                            {
                              id: 'view',
                              label: t('common.view', 'View'),
                              icon: Eye,
                              onClick: () => onView(customer),
                            },
                            {
                              id: 'ledger',
                              label: t('common.ledger', 'Ledger'),
                              icon: BookOpen,
                              onClick: () => onLedger(customer),
                            },
                            {
                              id: 'register',
                              label: t('customers.saleRegister', 'Register'),
                              icon: FileText,
                              onClick: () => onRegister(customer),
                            },
                            {
                              id: 'payment',
                              label: t('customers.payment', 'Payment'),
                              icon: Activity,
                              onClick: () => onPayment(customer),
                            },
                            {
                              id: 'invoices',
                              label: t('common.invoices', 'Invoices'),
                              icon: FileText,
                              onClick: () => onInvoices(customer),
                            },
                            {
                              id: 'quotations',
                              label: t('customers.quotations', 'Quotations'),
                              icon: FileText,
                              onClick: () => onQuotations(customer),
                            },
                            {
                              id: 'specialPrices',
                              label: t('customers.specialPrices', 'Special Prices'),
                              icon: Tag,
                              onClick: () => onSpecialPrices(customer),
                            },
                            {
                              id: 'edit',
                              label: t('common.edit', 'Edit'),
                              icon: Pencil,
                              onClick: () => onEdit(customer),
                              hidden: !canEdit,
                            },
                            {
                              id: 'delete',
                              label: t('common.delete', 'Delete'),
                              icon: Trash2,
                              onClick: () => onDelete(customer),
                              danger: true,
                              hidden: !canDelete(customer),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
